import * as InactivityActions from "actions/InactivityActions";
import classnames from "classnames";
import React from "react";
import omit from "lodash/omit";
import { connect } from "react-redux";
import { keyboardEventPassthroughHandler } from "utils/keyboardEventPassthroughHandler";
import styles from "./ControlButton.css";
import PropTypes from "prop-types";

const ControlButtonComponent = React.forwardRef((props, ref) => {
  const classNameModifiersArray = (() => {
    if (!props.classNameModifiers) {
      return [];
    }
    if (typeof props.classNameModifiers === "object") {
      return props.classNameModifiers;
    }
    return props.classNameModifiers.split(/\s+/);
  })();

  const classDefinitions = {
    [styles.root]: true,
    [styles.disabled]: props.disabled,
    [styles.borderless]: !props.border,
    "control-button--border": !!props.border,
    "control-button--text-label": false,
    "font-size--button--small": true,
    "font-style--button-3": true,
  };
  let contentBlock;

  // Add additional classes
  if (props.className) {
    props.className.split(/\s+/).forEach((className) => {
      classDefinitions[className] = true;
    });
  }

  if (props.classNameModifiers) {
    classNameModifiersArray.forEach((className) => {
      if (styles[className]) {
        classDefinitions[styles[className]] = true;
      } else {
        classDefinitions[`control-button--${className}`] = true;
      }
    });
  }

  const additionalProps = omit(
    props,
    "className",
    "onClick",
    "border",
    "text",
    "backButton",
  );

  if (props.text) {
    classDefinitions["control-button--text-label"] = true;
    contentBlock = (
      <div className="control-button__text-block">{props.text}</div>
    );
  } else {
    contentBlock = props.children;
  }

  const classes = classnames(classDefinitions);

  return (
    <div
      ref={ref}
      {...(props.ariaLabel && { "aria-label": props.ariaLabel })}
      className={classes}
      {...omit(
        additionalProps,
        "ariaLabel",
        "classNameModifiers",
        "usedInControls",
        "reportUIActivity",
      )}
      onClick={(event) => {
        if (props.disabled) return;

        props.reportUIActivity();

        props.onClick?.(event);
      }}
      onKeyUp={keyboardEventPassthroughHandler(props.onClick)}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      role="button"
      tabIndex={0}
      style={props.style}
    >
      {contentBlock}
    </div>
  );
});

ControlButtonComponent.propTypes = {
  ariaLabel: PropTypes.string,
  border: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  classNameModifiers: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  ]),
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  reportUIActivity: PropTypes.func.isRequired,
  style: PropTypes.object,
  text: PropTypes.node,
};

ControlButtonComponent.defaultProps = {
  backButton: false,
  border: false,
  disabled: false,
  usedInControls: true,
};

export { ControlButtonComponent as UnconnectedControlButtonComponent };

export default connect(
  (state, ownProps) => {
    const { classNameModifiers } = ownProps;

    return {
      border: ownProps.backButton ? true : ownProps.border,
      classNameModifiers,
    };
  },
  (dispatch) => ({
    reportUIActivity: () => dispatch(InactivityActions.reportUIActivity()),
  }),
  null,
  { forwardRef: true },
)(ControlButtonComponent);
